import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments';

export interface PhotoUploadResponse {
  id: number;
  referenceId: string;
}

export interface SavedPhoto {
  id: number;
  fileName: string;
  referenceId: string;
  size: number;
  description: string;
  mimeType: string;
  type: string;
}

export interface NewAttachmentRequest {
  parentObject: string;
  parentID: string;
  fileName: string;
  description?: string;
  size: number;
  createdBy?: string;
  type: number;
  mimeType: string;
  referenceId: string;
  customerShare?: boolean;
  restricted?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AttachmentsService {
  private static readonly filesUrl = environment.apiHost + 'files';

  constructor(private http: HttpClient) {}

  public static getFileUrl(referenceId: string): string {
    return `${AttachmentsService.filesUrl}/${referenceId}`;
  }

  /**
   * Uploads a file (from base64 data URL) to the server
   */
  public saveFileFromDataUrl(
    objectType: string,
    objectId: number | string,
    dataUrl: string,
    fileName: string,
    mimeType: string
  ): Observable<PhotoUploadResponse> {
    const url = `${AttachmentsService.filesUrl}/${objectType}/${objectId}`;

    // Convert base64 dataUrl to Blob
    const blob = this.dataUrlToBlob(dataUrl);
    const file = new File([blob], fileName, { type: mimeType });

    const formData: FormData = new FormData();
    formData.append('file', file, fileName);

    return this.http.post<PhotoUploadResponse>(url, formData, {
      reportProgress: true,
    });
  }

  /**
   * Uploads a file (from base64 data URL) with progress reporting
   */
  public saveFileFromDataUrlWithProgress(
    objectType: string,
    objectId: number | string,
    dataUrl: string,
    fileName: string,
    mimeType: string
  ): Observable<any> {
    const url = `${AttachmentsService.filesUrl}/${objectType}/${objectId}`;

    // Convert base64 dataUrl to Blob
    const blob = this.dataUrlToBlob(dataUrl);
    const file = new File([blob], fileName, { type: mimeType });

    const formData: FormData = new FormData();
    formData.append('file', file, fileName);

    return this.http.post<any>(url, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  public newAttachment(attachment: NewAttachmentRequest): Observable<any> {
    return this.http.post<any>(
      `${AttachmentsService.filesUrl}/submit`,
      attachment
    );
  }

  /**
   * Deletes a photo from the server
   */
  public deletePhoto(referenceId: string, id: number | null): Observable<any> {
    const params = {
      id,
      referenceId,
    };

    return this.http.delete<any>(AttachmentsService.filesUrl, { body: params });
  }

  /**
   * Formats bytes to human readable string
   */
  public formatBytes(bytes: number): string {
    const decimals = 1;
    if (!+bytes) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'kB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  /**
   * Converts a base64 data URL to a Blob
   */
  private dataUrlToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }
}
